import type { CNSNotification } from "@/models/cns";

const EVENT_TYPE_LIKE_SUMMARY = /^[a-z][A-Za-z0-9]*Event$/;

/**
 * Whether to hide the muted workspace-name line under the primary summary.
 * Backend summaries already embed the workspace display name for member added/removed;
 * share-invite accepted for {@code entityType === "workspace"} uses that name as the headline label too.
 * Other types keep the subtitle: experiments/learnings/insights name the artifact, not the workspace.
 */
export function shouldOmitWorkspaceSubtitleLine(notification: Pick<CNSNotification, "eventType" | "entityType">): boolean {
  switch (notification.eventType) {
    case "workspaceMemberAddedEvent":
    case "workspaceMemberRemovedEvent":
      return true;
    case "shareInviteAcceptedEvent":
      return notification.entityType === "workspace";
    default:
      return false;
  }
}

/**
 * Returns user-facing notification copy, preferring meaningful summary text.
 * Falls back to a humanized event type when summary is absent or technical.
 */
export function notificationDisplayText(input: Pick<CNSNotification, "summary" | "eventType">): string {
  const summary = input.summary?.trim();
  if (summary && !looksLikeRawEventKey(summary)) {
    return summary;
  }
  return fallbackEventMessage(input.eventType);
}

function looksLikeRawEventKey(value: string): boolean {
  return EVENT_TYPE_LIKE_SUMMARY.test(value);
}

export function humanizeEventType(eventType: string): string {
  return eventType
    .replace(/Event$/, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function fallbackEventMessage(eventType: string): string {
  switch (eventType) {
    case "experimentAssignedEvent":
      return "An experiment was assigned to you";
    case "experimentStatusChangedEvent":
      return "An experiment status changed";
    case "experimentDueSoonEvent":
      return "An experiment is due soon";
    case "experimentOverdueEvent":
      return "An experiment is overdue";
    case "shareInviteAcceptedEvent":
      return "You've been added to a shared project or conversation";
    case "workspaceMemberAddedEvent":
      return "You were added to a workspace";
    case "workspaceMemberRemovedEvent":
      return "You were removed from a workspace";
    case "insightUpdatedEvent":
      return "An insight was updated";
    case "workspaceSubscriptionActivatedEvent":
      return "Your subscription is active";
    case "workspaceSubscriptionPlanChangedEvent":
      return "Your plan was updated";
    case "workspaceSubscriptionCanceledEvent":
      return "Your subscription was canceled";
    case "workspaceSubscriptionPaymentFailedEvent":
      return "A payment couldn't be processed";
    case "workspaceTopupPurchasedEvent":
      return "Your credit top-up was added";
    default:
      return humanizeEventType(eventType);
  }
}
